import type { AuthState } from '$lib/firebase/auth/auth.svelte';
import type { Route as RouteBase } from '@shared/routes';
import { authToHandle, getHref } from './app-helpers';
import { AppQueryParams } from './app-query';
import { applyAppRouteDefaults } from './app-route-defaults';
import { validateRoutes } from './app-validation';

export enum RoutePath {
  Home = '',

  Register = 'register',
  Onboarding = 'onboarding',
  Pricing = 'pricing',
  Features = 'features',
  SignIn = 'sign-in',
  SignOut = 'sign-out',

  AuthAction = 'auth-action',
  VerifyEmail = 'verify-email',
  VerifyEmailApply = 'verify-email/apply',
  ForgotPassword = 'forgot-password',
  ResetPassword = 'reset-password',

  Dashboard = '@[handle]',
  Account = '@[handle]/account',
  Billing = '@[handle]/billing',
  Profile = '@[handle]/profile',
  Gear = '@[handle]/gear',
  GearItem = '@[handle]/gear/[id]',
  Packs = '@[handle]/packs',
  Pack = '@[handle]/packs/[id]',
  Trips = '@[handle]/trips',
  Trip = '@[handle]/trips/[id]',
  Shares = '@[handle]/shares',

  Changelog = 'changelog',
  Help = 'help',
  Terms = 'terms',
  Privacy = 'privacy',
  About = 'about',

  Dev = 'dev',
  Design = 'dev/design',
  Locale = 'dev/locale',
  // For ts-route examples
  Posts = 'posts',
  Post = 'post/[id]',
}

// Query types for specific route(s)
export enum AuthAction {
  // https://firebase.google.com/docs/auth/custom-email-handler
  verifyEmail = 'verifyEmail',
  resetPassword = 'resetPassword',
  recoverEmail = 'recoverEmail',
}

export interface AuthActionQuery {
  mode: AuthAction | undefined;
  code: string | undefined;
  continueUrl?: string;
  apiKey?: string;
  lang?: string;
}

export interface ForgotPasswordQuery {
  email: string | undefined;
}

export interface ResetPasswordQuery {
  code: string | undefined;
}

export interface VerifyEmailQuery {
  registered: boolean | undefined;
  code?: string;
}

export interface VerifyEmailApplyQuery {
  code: string | undefined;
}

export interface SignInQuery {
  returnTo: string | undefined;
  /** Pre-fills the email field on the sign-in form. Set after a successful
   * password reset so the user only types the new password. */
  email: string | undefined;
}

// Metadata types for specific route(s)

/** Identifier for the section a sidebar-enabled route belongs to. Kept as a
 * string union here (rather than imported from sidebar state) so the route
 * table stays a pure-data module with no runtime-state dependencies. */
export type SidebarSectionId = 'gear' | 'packs' | 'trips';

export interface SidebarMeta {
  /** Whether the section layout should render its sidebar on this route. */
  hasSidebar: boolean;
  /** Which section this route belongs to — drives ActivityBar active item,
   * sidebar panel selection, and the section-indexed collapse state. */
  sidebarSection?: SidebarSectionId;
  /** True on the section's top-level index route (e.g. /packs, /gear, /trips).
   * False on detail routes (e.g. /packs/[id]). The layout uses this to force
   * the sidebar visible on index routes (sidebar IS the content there) and
   * to gate the activity-bar reclick toggle accordingly — replaces the older
   * `page.params.id === undefined` heuristic with explicit intent. */
  isSectionIndex?: boolean;
  /** Name of the URL param that identifies the selected item on a detail
   * route (e.g. `'id'` for `/packs/[id]`). Set only on detail routes. The
   * `useSectionSidebar` composable auto-derives the currently-active item's
   * id from `page.params[detailIdParam]` when the consuming sidebar doesn't
   * provide its own `activeId` callback. */
  detailIdParam?: string;
  /** True when the sidebar IS the primary interaction surface on this route
   * — i.e., main renders only a hint/CTA shell and the user's next action is
   * to pick an item from the sidebar. Drives the auto-expand-on-entry rule
   * and the mobile overlay lock. Distinct from `isSectionIndex`: Gear is an
   * index but its main has a full gear table, so `sidebarIsPrimary` is false
   * there — the user can (and should) collapse the sidebar freely. Routes
   * where main is mostly-empty (Packs/Trips indices with just a "Select a
   * pack" hint) set this to true so the sidebar stays visible when entered. */
  sidebarIsPrimary?: boolean;
}

// Context types for specific route(s)
export interface IdNameContext {
  id: string;
  name: string;
}

// Narrow the Route type with an app default
export type Route<
  Path extends RoutePath,
  Query extends object = object,
  Meta extends object = object,
  Context extends object = object,
> = RouteBase<Path, AppQueryParams, Query, Meta, Context>;

// Defined route types
export type HomeRoute = Route<RoutePath.Home>;
export type RegisterRoute = Route<RoutePath.Register>;
export type OnboardingRoute = Route<RoutePath.Onboarding>;
export type PricingRoute = Route<RoutePath.Pricing>;
export type FeaturesRoute = Route<RoutePath.Features>;
export type SignInRoute = Route<RoutePath.SignIn, SignInQuery>;
export type SignOutRoute = Route<RoutePath.SignOut>;
export type AuthActionRoute = Route<RoutePath.AuthAction, AuthActionQuery>;
export type VerifyEmailRoute = Route<RoutePath.VerifyEmail, VerifyEmailQuery>;
export type VerifyEmailApplyRoute = Route<RoutePath.VerifyEmailApply, VerifyEmailApplyQuery>;
export type ForgotPasswordRoute = Route<RoutePath.ForgotPassword, ForgotPasswordQuery>;
export type ResetPasswordRoute = Route<RoutePath.ResetPassword, ResetPasswordQuery>;
export type DashboardRoute = Route<RoutePath.Dashboard>;
export type AccountRoute = Route<RoutePath.Account>;
export type BillingRoute = Route<RoutePath.Billing>;
export type ProfileRoute = Route<RoutePath.Profile>;
export type GearRoute = Route<RoutePath.Gear, object, SidebarMeta>;
export type GearItemRoute = Route<RoutePath.GearItem, object, SidebarMeta>;
export type PacksRoute = Route<RoutePath.Packs, object, SidebarMeta>;
export type PackRoute = Route<RoutePath.Pack, object, SidebarMeta>;
export type TripsRoute = Route<RoutePath.Trips, object, SidebarMeta>;
export type TripRoute = Route<RoutePath.Trip, object, SidebarMeta>;
export type SharesRoute = Route<RoutePath.Shares>;
export type ChangelogRoute = Route<RoutePath.Changelog>;
export type HelpRoute = Route<RoutePath.Help>;
export type TermsRoute = Route<RoutePath.Terms>;
export type PrivacyRoute = Route<RoutePath.Privacy>;
export type AboutRoute = Route<RoutePath.About>;
export type DevRoute = Route<RoutePath.Dev>;
export type DesignRoute = Route<RoutePath.Design>;
export type LocaleRoute = Route<RoutePath.Locale>;
export type PostsRoute = Route<RoutePath.Posts>;
export type PostRoute = Route<RoutePath.Post, object, object, IdNameContext>;

// Union type for all defined routes
export type AppRoute =
  | HomeRoute
  | RegisterRoute
  | OnboardingRoute
  | PricingRoute
  | FeaturesRoute
  | SignInRoute
  | SignOutRoute
  | AuthActionRoute
  | VerifyEmailRoute
  | VerifyEmailApplyRoute
  | ForgotPasswordRoute
  | ResetPasswordRoute
  | DashboardRoute
  | AccountRoute
  | BillingRoute
  | ProfileRoute
  | GearRoute
  | GearItemRoute
  | PacksRoute
  | PackRoute
  | TripsRoute
  | TripRoute
  | SharesRoute
  | ChangelogRoute
  | HelpRoute
  | TermsRoute
  | PrivacyRoute
  | AboutRoute
  | DevRoute
  | DesignRoute
  | LocaleRoute
  | PostsRoute
  | PostRoute;

// Centralized routes definition
const baseRoutes: AppRoute[] = [
  {
    path: RoutePath.Home,
    title: () => 'Prepare for your next adventure',
    description: () =>
      'StuffPack helps outdoor enthusiasts organize their gear, plan their loadout, and never forget what to pack. Built for those who love the outdoors.',
  },
  {
    path: RoutePath.Register,
    title: () => 'Register',
  },
  {
    path: RoutePath.Onboarding,
    title: () => 'Welcome',
  },
  {
    path: RoutePath.Pricing,
    title: () => 'Pricing',
    description: () =>
      'Simple, transparent pricing for StuffPack. Start free with up to 1,000 gear items. Upgrade to Plus for unlimited gear, analytics, and more.',
  },
  {
    path: RoutePath.Features,
    title: () => 'Features',
    description: () =>
      "StuffPack is the packing companion that helps you pack your bag and confirm you're 100% ready to go.",
  },
  {
    path: RoutePath.SignIn,
    title: () => 'Sign In',
    getQuery: (params) => ({
      returnTo: params.value('returnTo'),
      email: params.value('email'),
    }),
  },
  {
    path: RoutePath.SignOut,
    title: () => 'Sign Out',
  },
  {
    path: RoutePath.AuthAction,
    title: () => 'Authentication Action',
    getQuery: (params) => ({
      mode: params.enumValue(AuthAction, 'mode'),
      code: params.value('oobCode'),
      continueUrl: params.value('continueUrl'),
      apiKey: params.value('apiKey'),
      lang: params.value('lang'),
    }),
  },
  {
    path: RoutePath.VerifyEmail,
    title: () => 'Verify Email',
    getQuery: (params) => ({
      registered: params.boolean('registered'),
      code: params.value('code'),
    }),
  },
  {
    path: RoutePath.VerifyEmailApply,
    title: () => 'Apply Email Verification',
    getQuery: (params) => ({
      code: params.value('code'),
    }),
  },
  {
    path: RoutePath.ForgotPassword,
    title: () => 'Forgot Password',
    getQuery: (params) => ({
      email: params.value('email'),
    }),
  },
  {
    path: RoutePath.ResetPassword,
    title: () => 'Reset Password',
    getQuery: (params) => ({
      code: params.value('code'),
    }),
  },
  {
    path: RoutePath.Dashboard,
    title: () => 'Dashboard',
  },
  {
    path: RoutePath.Account,
    parentPath: RoutePath.Dashboard,
    title: () => 'Account',
  },
  {
    path: RoutePath.Billing,
    parentPath: RoutePath.Dashboard,
    title: () => 'Billing',
  },
  {
    path: RoutePath.Profile,
    parentPath: RoutePath.Dashboard,
    title: () => 'Profile',
  },
  {
    path: RoutePath.Gear,
    parentPath: RoutePath.Dashboard,
    title: () => 'Gear',
    getMeta: () => ({ hasSidebar: true, sidebarSection: 'gear', isSectionIndex: true }),
  },
  {
    path: RoutePath.GearItem,
    parentPath: RoutePath.Gear,
    title: () => 'Gear Item',
    getMeta: () => ({ hasSidebar: true, sidebarSection: 'gear', isSectionIndex: false, detailIdParam: 'id' }),
  },
  {
    path: RoutePath.Packs,
    parentPath: RoutePath.Dashboard,
    title: () => 'Packs',
    getMeta: () => ({ hasSidebar: true, sidebarSection: 'packs', isSectionIndex: true, sidebarIsPrimary: true }),
  },
  {
    path: RoutePath.Pack,
    parentPath: RoutePath.Packs,
    title: () => 'Pack',
    getMeta: () => ({ hasSidebar: true, sidebarSection: 'packs', isSectionIndex: false, detailIdParam: 'id' }),
  },
  {
    path: RoutePath.Trips,
    parentPath: RoutePath.Dashboard,
    title: () => 'Trips',
    getMeta: () => ({ hasSidebar: true, sidebarSection: 'trips', isSectionIndex: true, sidebarIsPrimary: true }),
  },
  {
    path: RoutePath.Trip,
    parentPath: RoutePath.Trips,
    title: () => 'Trip',
    getMeta: () => ({ hasSidebar: true, sidebarSection: 'trips', isSectionIndex: false, detailIdParam: 'id' }),
  },
  {
    path: RoutePath.Shares,
    parentPath: RoutePath.Account,
    title: () => 'Shares',
  },
  {
    path: RoutePath.Changelog,
    title: () => 'Changelog',
    description: () => "What's new in StuffPack — recent updates, improvements, and fixes.",
  },
  {
    path: RoutePath.Help,
    title: () => 'Help',
    description: () =>
      'Get help with StuffPack. Find answers, contact support, and learn how to get the most out of your gear management.',
  },
  {
    path: RoutePath.Terms,
    title: () => 'Terms of Service',
    description: () => 'StuffPack terms of service. Rules and guidelines for using our platform.',
  },
  {
    path: RoutePath.Privacy,
    title: () => 'Privacy Policy',
    description: () => 'StuffPack privacy policy. How we collect, use, and protect your data.',
  },
  {
    path: RoutePath.About,
    title: () => 'About',
  },
  {
    path: RoutePath.Dev,
    title: () => 'Development',
  },
  {
    path: RoutePath.Design,
    title: () => 'Design System',
  },
  {
    path: RoutePath.Locale,
    title: () => 'Locale',
  },
  {
    path: RoutePath.Posts,
    title: () => 'Posts',
  },
  {
    path: RoutePath.Post,
    title: ({ params, context }) => context?.name ?? `Post ID ${params?.id}`,
    breadcrumb: ({ params, context }) => context?.name ?? `Post ID ${params?.id}`,
    href: ({ params, context }) => {
      const base = `/posts/${params?.id}`;
      return context?.name ? `${base}#${context.name}` : base;
    },
  },
] as const;

export const appRoutes: AppRoute[] = applyAppRouteDefaults(baseRoutes);

// Validate routes at initialization
validateRoutes(appRoutes);

/**
 * Framework-agnostic path builders for every app route.
 *
 * Each entry's `href()` returns a plain `string`. Suitable for non-Svelte
 * consumers (server logs, share-link generation, schema metadata, etc.).
 *
 * For SvelteKit navigation (`goto`, `<a href>`, etc.) prefer the `routes`
 * adapter in `app-routes.svelte.ts`, which wraps these and returns
 * `ResolvedPathname` so the `svelte/no-navigation-without-resolve`
 * eslint rule is satisfied without a `resolve()` wrapper at every call site.
 */
export const routePaths = {
  home: { href: () => getHref(RoutePath.Home) },

  register: { href: () => getHref(RoutePath.Register) },
  onboarding: { href: () => getHref(RoutePath.Onboarding) },
  pricing: { href: () => getHref(RoutePath.Pricing) },
  features: { href: () => getHref(RoutePath.Features) },
  signIn: {
    href: (params?: { returnTo?: string; email?: string } | string) => {
      // Backwards-compat: treat a bare string as returnTo (existing call sites).
      const query = typeof params === 'string' ? { returnTo: params } : (params ?? {});
      return getHref(RoutePath.SignIn, { query });
    },
  },
  signOut: { href: () => getHref(RoutePath.SignOut) },

  authAction: { href: () => getHref(RoutePath.AuthAction) },
  verifyEmail: { href: (registered?: boolean) => getHref(RoutePath.VerifyEmail, { query: { registered } }) },
  verifyEmailApply: { href: () => getHref(RoutePath.VerifyEmailApply) },
  forgotPassword: { href: (email?: string) => getHref(RoutePath.ForgotPassword, { query: { email } }) },
  resetPassword: { href: () => getHref(RoutePath.ResetPassword) },

  dashboard: {
    href: (authOrHandle: AuthState | string) =>
      getHref(RoutePath.Dashboard, { params: { handle: authToHandle(authOrHandle) } }),
  },
  account: {
    href: (authOrHandle: AuthState | string) =>
      getHref(RoutePath.Account, { params: { handle: authToHandle(authOrHandle) } }),
  },
  billing: {
    href: (authOrHandle: AuthState | string) =>
      getHref(RoutePath.Billing, { params: { handle: authToHandle(authOrHandle) } }),
  },
  profile: {
    href: (authOrHandle: AuthState | string) =>
      getHref(RoutePath.Profile, { params: { handle: authToHandle(authOrHandle) } }),
  },
  gear: {
    href: (authOrHandle: AuthState | string) =>
      getHref(RoutePath.Gear, { params: { handle: authToHandle(authOrHandle) } }),
  },
  gearItem: {
    href: (authOrHandle: AuthState | string, id: string) =>
      getHref(RoutePath.GearItem, { params: { handle: authToHandle(authOrHandle), id } }),
  },
  packs: {
    href: (authOrHandle: AuthState | string) =>
      getHref(RoutePath.Packs, { params: { handle: authToHandle(authOrHandle) } }),
  },
  pack: {
    href: (authOrHandle: AuthState | string, id: string) =>
      getHref(RoutePath.Pack, { params: { handle: authToHandle(authOrHandle), id } }),
  },
  trips: {
    href: (authOrHandle: AuthState | string) =>
      getHref(RoutePath.Trips, { params: { handle: authToHandle(authOrHandle) } }),
  },
  trip: {
    href: (authOrHandle: AuthState | string, id: string) =>
      getHref(RoutePath.Trip, { params: { handle: authToHandle(authOrHandle), id } }),
  },
  shares: {
    href: (authOrHandle: AuthState | string) =>
      getHref(RoutePath.Shares, { params: { handle: authToHandle(authOrHandle) } }),
  },

  changelog: { href: () => getHref(RoutePath.Changelog) },
  help: { href: () => getHref(RoutePath.Help) },
  terms: { href: () => getHref(RoutePath.Terms) },
  privacy: { href: () => getHref(RoutePath.Privacy) },

  dev: { href: () => getHref(RoutePath.Dev) },
  design: { href: () => getHref(RoutePath.Design) },
  locale: { href: () => getHref(RoutePath.Locale) },

  posts: { href: () => getHref(RoutePath.Posts) },
  post: {
    href: (id: string, name?: string) => getHref(RoutePath.Post, { context: { id, name } }),
  },
};
